import React, { useMemo } from 'react';

import { Box, Typography, Button, useTheme } from '@material-ui/core';
import { AiOutlinePlus } from 'react-icons/ai';

import { PageLoading } from '@vyce/core/src/components/PageLoading';
import { ConfirmDialog } from '@vyce/core/src/components/ConfirmDialog';
import { ActivateSubscriptionDialog, AdaptiveSearch } from '@vyce/core/src/components';
import { useLocationMembersData } from '@vyce/core/src/modules/timeModule/hooks/useLocationMembersData';
import { useLocationData } from '@vyce/core/src/modules/timeModule/hooks/useLocationData';
import { EditLocation } from '@vyce/core/src/views/time/LocationPage/EditLocation';
import { LocationPreview } from '@vyce/core/src/views/time/LocationPage/LocationPreview';
import { LocationWorkers } from '@vyce/core/src/views/time/components/LocationWorkers';
import { InvitePreselectedTarget } from '@vyce/core/src/views/invites/types';

import { useActions, useInviteButton, useTypedSelector } from '../../hooks';
import { UniversalInviteModule } from 'src/modules';
import { getStaffRequest } from '@vyce/core/src/api/companies';
import { TIME_INTERFACE_PERMISSIONS } from '@vyce/core/src/types';
import { usePermissions } from '@vyce/core/src/hooks/usePermissions';

export const EmployerLocationPage: React.FC = () => {
  const { selectedCompany } = useTypedSelector(state => state.helper);
  const { user } = useTypedSelector(state => state);
  const { fetchCompanyData } = useActions();
  const theme = useTheme();
  const { canOnResource } = usePermissions();
  const {
    loading,
    workersList,
    locationManagers,
    setOpenConfirmDialog,
    editMode,
    location,
    saveLocationChanges,
    openConfirmDialog,
    customLocationFields,
    closeLocation,
    createShift,
    deleteShift,
    setStaffSubstring,
  } = useLocationData({
    selectedCompany,
    fetchCompanyData,
    getStaffRequest,
  });

  // Per-location, not company-wide: the same person may edit one site and not
  // another. A company-wide grant still passes — canOnResource folds that in.
  const canUpdateLocation = canOnResource(
    TIME_INTERFACE_PERMISSIONS.UPDATE_SITE,
    'location',
    location?.uuid
  );

  // The squad list has its own right — time:can_search_workers, which is what
  // sites/workers/search checks. Editing the location says nothing about it.
  const canReadSquad = canOnResource(TIME_INTERFACE_PERMISSIONS.USERS, 'location', location?.uuid);

  const chatCreator = {
    email: user.email,
    phone: user.phone,
    uid: user.uuid,
    name: `${user.first_name} ${user.last_name}`,
    avatarUrl: user.photo?.url,
  };

  const {
    workers,
    total,
    page,
    substring,
    sortModel,
    loadingTableData,
    closeGridAction,
    removeWorker,
    handlePageChange,
    handleSearchChange,
    handleSortModelChange,
  } = useLocationMembersData({
    siteId: location?.uuid,
    selectedCompany,
    user,
    enabled: canReadSquad,
  });

  const {
    isActivateSubscriptionDialogOpen,
    isInviteFormOpen,
    subscribeLoading,
    closeActivateSubscriptionDialog,
    setCloseInviteForm,
    handleInviteButtonClick,
    simpleSubscribe,
    canInvite,
  } = useInviteButton();

  // Whoever is invited from a location page — or straight after the location was created,
  // through ?invite=true — is meant for this location, so the dialog opens with it ticked.
  const inviteTarget = useMemo<InvitePreselectedTarget | undefined>(
    () => (location?.uuid ? { module: 'time', resourceId: location.uuid } : undefined),
    [location?.uuid]
  );

  return (
    <>
      {loading ? (
        <Box height="calc(100vh - 200px)">
          <PageLoading />
        </Box>
      ) : (
        <>
          {location && (
            <>
              {editMode && selectedCompany?.uuid ? (
                <EditLocation
                  companyId={selectedCompany.uuid}
                  createShift={createShift}
                  deleteShift={deleteShift}
                  saveLocationChanges={saveLocationChanges}
                  location={location}
                  customLocationFields={customLocationFields}
                  handleClose={() => setOpenConfirmDialog(true)}
                  workers={workersList}
                  setStaffSubstring={setStaffSubstring}
                  locationManagers={locationManagers}
                />
              ) : (
                <LocationPreview
                  handleClose={() => setOpenConfirmDialog(true)}
                  location={location}
                  customLocationFields={customLocationFields}
                  locationManagers={locationManagers}
                  canUpdateLocation={canUpdateLocation}
                />
              )}

              {/* Without the search-workers right the squad is hidden entirely —
                  its endpoint 403s — leaving only the invite button. */}
              {(canReadSquad || canInvite) && (
                <Box marginTop={5}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" marginBottom={3}>
                    {canReadSquad && <Typography variant="h6">Squad Members</Typography>}

                    <Box display="flex" gridGap={16} marginLeft="auto">
                      {canReadSquad && (
                        <AdaptiveSearch
                          variant="paper"
                          smallRounding
                          searchText={substring || ''}
                          handleSearchChange={handleSearchChange}
                        />
                      )}

                      {canInvite && (
                        <Button
                          onClick={handleInviteButtonClick}
                          size="small"
                          data-tour="invite"
                          startIcon={<AiOutlinePlus />}
                          variant="outlined"
                          style={{ backgroundColor: theme.palette.background.paper }}
                          color="primary">
                          Invite user
                        </Button>
                      )}
                    </Box>
                  </Box>

                  {canReadSquad && (
                    <LocationWorkers
                      companyId={selectedCompany?.uuid}
                      chatCreator={chatCreator}
                      loading={loadingTableData}
                      total={total}
                      page={page}
                      sortModel={sortModel}
                      handleSortModelChange={handleSortModelChange}
                      handlePageChange={handlePageChange}
                      closeGridAction={closeGridAction}
                      removeWorker={removeWorker}
                      workers={workers}
                    />
                  )}
                </Box>
              )}
            </>
          )}
        </>
      )}

      <UniversalInviteModule
        isOpen={isInviteFormOpen}
        onClose={setCloseInviteForm}
        preselectedTarget={inviteTarget}
      />

      <ConfirmDialog
        handleClose={() => setOpenConfirmDialog(false)}
        open={openConfirmDialog}
        confirmText="Close Location"
        cancelText="Cancel"
        title="Do you want to close this Location?"
        subtitle="This means that no-one will be able to clock in or clock out of this location going forward."
        handleConfirm={() => closeLocation()}
      />

      <ActivateSubscriptionDialog
        open={isActivateSubscriptionDialogOpen}
        onClose={closeActivateSubscriptionDialog}
        callback={simpleSubscribe}
        loading={subscribeLoading}
        wasSubscribed={selectedCompany?.subscribed_company}
      />
    </>
  );
};
